import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from '@klear/constants/currency'
import { Money } from '@klear/money'
import { Organization } from '@klear/organization'
import type { StripeFinancialAccount } from '@klear/stripe'
import { Timestamp } from '@klear/time'
import { buildLegalDocumentFileUrl } from '@klear/upload/file-url'
import toString from 'lodash/toString'

import {
  KLEAR_ACCOUNT_TYPE_RECEIVABLE,
  klearAccountTypeLabel,
  type KlearAccountType
} from '@klear/klear-financial-account/constants'

export type KlearFinancialAccountIdentity = { kind: 'new' } | { kind: 'saved'; id: number }

export type KlearFinancialAccountMeta = {
  financialAccount?: StripeFinancialAccount
}

export type KlearFinancialAccountBalances = {
  cash: Money
  inboundPending: Money
  outboundPending: Money
}

export type KlearFinancialAccountJson = {
  identity: KlearFinancialAccountIdentity
  organizationId: number
  financialAccountId: string
  financialAccount?: StripeFinancialAccount
  balances?: KlearFinancialAccountBalances
  accountType: KlearAccountType
  signedDocumentKey?: string | null
  organizationName?: string
  organizationStripeAccountId?: string | null
  createdAt: number
  updatedAt: number
  deletedAt?: number
  createdByUserId: number
  createdByUserEmail: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string
}

export function klearFinancialAccountToJson(account: KlearFinancialAccount): KlearFinancialAccountJson {
  return {
    identity: account.identity,
    organizationId: account.organizationId,
    financialAccountId: account.financialAccountId,
    financialAccount: account.account,
    balances: account.balances,
    accountType: account.accountType,
    signedDocumentKey: account.signedDocumentKey ?? null,
    organizationName: account.organization.name || undefined,
    organizationStripeAccountId: account.organization.stripeAccountId ?? null,
    createdAt: account.createdAt,
    updatedAt: account.updatedAt,
    deletedAt: account.deletedAt,
    createdByUserId: account.createdByUserId,
    createdByUserEmail: account.createdByUserEmail,
    updatedByUserId: account.updatedByUserId,
    updatedByUserEmail: account.updatedByUserEmail,
    deletedByUserId: account.deletedByUserId,
    deletedByUserEmail: account.deletedByUserEmail
  }
}

export class KlearFinancialAccount {
  static readonly __klearClass = 'KlearFinancialAccount'

  readonly identity: KlearFinancialAccountIdentity
  organizationId: number
  financialAccountId: string
  accountType: KlearAccountType
  organization: Organization
  signedDocumentKey: string | null
  createdAt: number
  updatedAt: number
  deletedAt?: number
  createdByUserId: number
  createdByUserEmail: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string

  #financialAccount: StripeFinancialAccount | undefined
  #balances: KlearFinancialAccountBalances | undefined

  constructor(props: Partial<KlearFinancialAccountJson>) {
    const now = Timestamp.now().epochMs
    if (props.identity?.kind === 'saved') {
      this.identity = { kind: 'saved', id: props.identity.id }
    } else if (props.identity?.kind === 'new') {
      this.identity = { kind: 'new' }
    } else {
      this.identity = { kind: 'new' }
    }
    this.organizationId = props.organizationId ?? 0
    this.financialAccountId = props.financialAccountId ?? props.financialAccount?.id ?? ''
    this.accountType = props.accountType ?? KLEAR_ACCOUNT_TYPE_RECEIVABLE
    this.#financialAccount = props.financialAccount
    this.#balances = props.balances
    this.organization = new Organization({
      id: props.organizationId ?? 0,
      name: props.organizationName ?? '',
      stripeAccountId: props.organizationStripeAccountId ?? undefined
    })
    this.signedDocumentKey = props.signedDocumentKey ?? null
    this.createdAt = props.createdAt ?? now
    this.updatedAt = props.updatedAt ?? this.createdAt
    this.deletedAt = props.deletedAt
    this.createdByUserId = props.createdByUserId ?? 0
    this.createdByUserEmail = props.createdByUserEmail ?? 'unknown@klear.internal'
    this.updatedByUserId = props.updatedByUserId
    this.updatedByUserEmail = props.updatedByUserEmail
    this.deletedByUserId = props.deletedByUserId
    this.deletedByUserEmail = props.deletedByUserEmail
  }

  get id(): number | undefined {
    return this.identity.kind === 'saved' ? this.identity.id : undefined
  }

  get isSaved(): boolean {
    return this.identity.kind === 'saved'
  }

  readonly isKlearAccount = true

  get name() {
    return klearAccountTypeLabel(this.accountType)
  }

  get nameDetailed() {
    if (this.#financialAccount == null) return this.nameAndLast4
    return `${this.nameAndLast4} - ${this.balanceAvailable.toString()}`
  }

  get nameAndLast4() {
    return this.last4 ? `${this.name} - ${this.last4}` : this.name
  }

  get account() {
    return this.#financialAccount
  }

  get last4() {
    return this.accountNumberLast4
  }

  hydrateStripeAccount(account: StripeFinancialAccount, balances: KlearFinancialAccountBalances): void {
    this.#financialAccount = account
    this.#balances = balances
    this.financialAccountId = account.id
  }

  get balances(): KlearFinancialAccountBalances | undefined {
    return this.#balances
  }

  get status() {
    return this.account?.status
  }

  get isOpen() {
    return this.status !== 'closed'
  }

  get firstAba() {
    return this.account?.financial_addresses?.find((fa) => fa.aba)?.aba
  }

  get bankName() {
    return this.firstAba?.bank_name ?? ''
  }

  get accountHolderName() {
    return this.firstAba?.account_holder_name ?? ''
  }

  get accountNumber() {
    return this.firstAba?.account_number ?? ''
  }

  get accountNumberLast4() {
    return this.firstAba?.account_number_last4 ?? ''
  }

  get routingNumber() {
    return this.firstAba?.routing_number ?? ''
  }

  get balanceCurrency(): CurrencyCode {
    return DEFAULT_CURRENCY_CODE
  }

  #hydratedBalances(): KlearFinancialAccountBalances {
    if (this.#balances == null) {
      throw new Error(
        `KlearFinancialAccount ${this.financialAccountId || '(unidentified)'} has no Stripe financial account hydrated; its balance cannot be read`
      )
    }
    return this.#balances
  }

  get balanceCash(): Money {
    return this.#hydratedBalances().cash
  }

  get balanceAvailable(): Money {
    return this.balanceCash
  }

  get balanceInboundPending(): Money {
    return this.#hydratedBalances().inboundPending
  }

  get balanceOutboundPending(): Money {
    return this.#hydratedBalances().outboundPending
  }

  get activeFeatures() {
    return this.account?.active_features ?? []
  }

  getSignedDocumentUrl(traderId: string | number | undefined): string | null {
    const key = this.signedDocumentKey
    if (!key || traderId === undefined) return null
    return buildLegalDocumentFileUrl(toString(traderId), key)
  }

  toJSON() {
    return klearFinancialAccountToJson(this)
  }
}
